import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { apiList } from "@/integrations/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Loader2 } from "lucide-react";
import { ReportExportButton } from "@/features/reports/export/ReportExportButton";
import { SpareRequestStatusBadge, ProcurementStatusBadge } from "@/features/tickets/badges";

type Row = {
  id: string;
  ticket_id: string;
  spare_name: string;
  quantity_required: number;
  quantity_issued: number;
  status: string;
  procurement_status: string;
  created_at: string;
  ticket?: { ticket_no: string; issue_title: string; branch_id: string | null; asset_id: string | null } | null;
};

export const Route = createFileRoute("/_authenticated/reports/ticket-spare-usage")({
  component: SpareUsagePage,
});

function SpareUsagePage() {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["reports", "ticket-spare-usage"],
    queryFn: () => apiList<Row>("/api/v1/tickets/spare-requests/", { limit: 1000 }),
  });

  const exportRows = rows.map((r) => ({
    "Ticket #": r.ticket?.ticket_no ?? "",
    "Ticket": r.ticket?.issue_title ?? "",
    "Spare": r.spare_name,
    "Required": r.quantity_required,
    "Issued": r.quantity_issued,
    "Status": r.status,
    "Procurement": r.procurement_status,
    "Created": r.created_at,
  }));

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <Button variant="ghost" size="sm" className="mb-2 -ml-2 text-muted-foreground" asChild>
            <Link to="/reports"><ArrowLeft className="mr-2 h-4 w-4" />Back to Reports</Link>
          </Button>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight"><span className="text-muted-foreground font-mono text-sm mr-2">R-48</span>Spare Used in Tickets</h1>
        </div>
        {rows.length > 0 && (
          <ReportExportButton
            reportKey="R-48"
            title="Spare Usage"
            filename="ticket-spare-usage"
            rows={() => rows}
          />
        )}
      </div>

      {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : (
        <Card><CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Ticket #</TableHead><TableHead>Spare</TableHead>
              <TableHead>Required</TableHead><TableHead>Issued</TableHead>
              <TableHead>Status</TableHead><TableHead>Procurement</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">
                    <Link to="/tickets/$ticketId" params={{ ticketId: r.ticket_id }} className="text-primary hover:underline">{r.ticket?.ticket_no ?? "—"}</Link>
                  </TableCell>
                  <TableCell className="text-sm">{r.spare_name}</TableCell>
                  <TableCell className="text-xs">{r.quantity_required}</TableCell>
                  <TableCell className="text-xs">{r.quantity_issued}</TableCell>
                  <TableCell><SpareRequestStatusBadge value={r.status as never} /></TableCell>
                  <TableCell><ProcurementStatusBadge value={r.procurement_status as never} /></TableCell>
                </TableRow>
              ))}
              {data.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-muted-foreground">No spare requests found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}
    </div>
  );
}
